/* eslint-disable prettier/prettier */
import React, { useEffect, useMemo, useState } from 'react';

import { useHeader } from '../../context/HeaderContext';
import icon from '../../assets/coach/img3.png';
import styles from '../../styles/pages/AdminDashboard.module.css';
import { useCoachProfilePrograms } from '../../hooks/useCourse';
import { useCourseModules } from '../../hooks/useCourseModules';
import { useProgramModules } from '../../hooks/useProgramModules';
import { useActivityTypes } from '../../hooks/useActivityType';
import { useCourseEnrollment } from '../../hooks/useCourseEnrollment';
import WeeklySchedule from '../../components/Schedule/WeeklySchedule';
import CreateActivityModal from '../../components/pageComponents/dashboard/coach/CreateActivityModal';
import DashboardStatCard from '../../components/pageComponents/dashboard/coach/DashboardStatCard';
import DashboardStatsContainer from '../../components/pageComponents/dashboard/coach/DashboardStatsContainer';
import { NewActivityTypeModal } from '../../components/pageComponents/program/activity/NewActivityTypeModal';
import CoachDashboardActivity from '../../components/pageComponents/dashboard/coach/CoachDashboardActivity';
import { useCoaching } from '../../hooks/useCoaching';
import SuccessContent from '../../components/controlVariations/ModularSuccessModal';

const NavCoachMentorDashboard: React.FC = () => {
	const { setHeaderContent } = useHeader();

	const [openModal, setOpenModal] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);
	const [submitError, setSubmitError] = useState('');
	const [successTitle, setSuccessTitle] = useState('');
	const [successSubtitle, setSuccessSubtitle] = useState('');

	/* FORM STATES */
	const [program, setProgram] = useState('');
	const [programId, setProgramId] = useState('');
	// const [module, setModule] = useState('');
	const [module, setModule] = useState('');
	const [moduleOptions, setModuleOptions] = useState<
		{ label: string; value: string }[]
	>([]);

	const [activityType, setActivityType] = useState('');
	const [openActivityTypeModal, setOpenActivityTypeModal] = useState(false);
	const [moduleId, setModuleId] = useState('');
	const [activityTypeId, setActivityTypeId] = useState('');

	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [errors, setErrors] = useState<any>({});

	const [date, setDate] = useState('');
	const [startTime, setStartTime] = useState('');
	const [endTime, setEndTime] = useState('');
	const [participant, setParticipant] = useState('');
	const [participantOptions, setParticipantOptions] = useState<
		{ label: string; value: string }[]
	>([]);

	const [isSubmitting, setIsSubmitting] = useState(false);

	// 🔹 Header Logic
	const userDataString = localStorage.getItem('user');
	let userName = 'User';
	let coachId = '';
	if (userDataString) {
		try {
			const userData = JSON.parse(userDataString);
			userName = userData.fullname.split(' ')[0];
			coachId = userData.id;
		} catch {
			userName = 'User';
		}
	}

	const headerNode = useMemo(
		() => <p>Welcome back, {userName}</p>,
		[userName]
	);
	useEffect(() => {
		setHeaderContent(headerNode);
	}, [setHeaderContent, headerNode]);

	const { programs } = useCoachProfilePrograms(coachId);
	const { fetchCourseModules, createLesson } = useCourseModules();
	const { fetchProgramModules } = useProgramModules();
	const { activityTypes, fetchActivityTypes } = useActivityTypes();
	const { getCourseEnrollments } = useCourseEnrollment();
	const { createMentoringLesson } = useCoaching('');

	useEffect(() => {
		fetchActivityTypes();
	}, [fetchActivityTypes]);

	const programOptions =
		programs?.map((program: any) => ({
			label: program.title,
			value: program.id,
		})) || [];

	const activityTypeOptions =
		activityTypes?.map((item: any) => ({
			label: item.title,
			value: item.id,
			color: item.color,
		})) || [];

	const validateForm = () => {
		const newErrors: any = {};

		if (!programId) {
			newErrors.program = 'Program is required';
		}

		if (!moduleId) {
			newErrors.module = 'Module is required';
		}

		if (!activityTypeId) {
			newErrors.activityType = 'Activity Type is required';
		}

		const trimmedTitle = title.trim();

		if (!trimmedTitle) {
			newErrors.title = 'Activity Title is required';
		} else if (trimmedTitle.length < 4) {
			newErrors.title = 'Activity title must be at least 4 characters';
		} else if (/^\d/.test(trimmedTitle)) {
			newErrors.title = 'Activity title cannot start with a number';
		} else if (!/^[A-Za-z][A-Za-z0-9 ]*$/.test(trimmedTitle)) {
			newErrors.title =
				'Activity title cannot contain special characters';
		}

		if (!date) {
    newErrors.date = 'Date is required';
} else {
    const selectedDate = new Date(date);
    const today = new Date();

    // Remove time part
    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
        newErrors.date = 'Past dates are not allowed';
    }
}

		if (!startTime) {
			newErrors.startTime = 'Start time is required';
		}

		if (!endTime) {
			newErrors.endTime = 'End time is required';
		}

		if (startTime && endTime) {
           const [startHour, startMinute] = startTime.split(':').map(Number);
           const [endHour, endMinute] = endTime.split(':').map(Number);

           const startTotalMinutes = startHour * 60 + startMinute;
           const endTotalMinutes = endHour * 60 + endMinute;

           if (endTotalMinutes <= startTotalMinutes)
			 {
                 newErrors.endTime = 'End time must be greater than start time';
             }
        }

		if (activityType === 'One to One Mentoring' && !participant) {
			newErrors.participant = 'Participant is required';
		}

		setErrors(newErrors);

		return Object.keys(newErrors).length === 0;
	};

	const resetForm = () => {
		setProgram('');
		setProgramId('');

		setModule('');
		setModuleId('');
		setModuleOptions([]);

		setActivityType('');
		setActivityTypeId('');

		setTitle('');
		setDescription('');

		setDate('');
		setStartTime('');
		setEndTime('');
		setErrors({});
		setSubmitError('');
		setParticipant('');
	};

	const handleMentoringActivity = async () => {
		try {
			const startDateTime = new Date(`${date}T${startTime}`);

			const endDateTime = new Date(`${date}T${endTime}`);

			const mentoringPayload = {
				moduleid: moduleId,

				learnerId:
					participantOptions.find(item => item.label === participant)
						?.value || '',

				title,

				description: description?.trim() || undefined,


				scheduledstart: startDateTime.toISOString(),

				scheduledend: endDateTime.toISOString(),

				activitytypeid: activityTypeId,

				orderindex: 0,
         

				duration:
					Math.floor(
						(endDateTime.getTime() - startDateTime.getTime()) /
							60000
					) || 0,
			};

			const response = await createMentoringLesson(mentoringPayload);
		

			if (response) {
				resetForm();
				setOpenModal(false);

				setShowSuccess(true);

				setTimeout(() => {
					setShowSuccess(false);
				}, 3000);
					window.location.reload();
			}
		} catch (error: any) {
			setSubmitError(
				error?.response?.data?.message ||
					error?.message ||
					'Failed to create mentoring activity'
			);
		}

	};

	const handleNormalActivity = async () => {
		try {
			const startDateTime = new Date(`${date}T${startTime}`);

			const endDateTime = new Date(`${date}T${endTime}`);
			console.log('date', date);
			console.log('startTime', startTime);
			console.log('startDateTime', startDateTime);
			console.log('ISO', startDateTime.toISOString());
			const payload = {
				moduleid: moduleId,
				title,
				description,
				orderindex: 0,
				contenttype: 'live-session',
				activitytypeid: activityTypeId,
				scheduledstarttime: startDateTime,
				scheduledendtime: endDateTime,
				timezone: 'Asia/Kolkata',
				isrequired: true,
				deliveredby: coachId,
        islive : true,
			};

			const response = await createLesson(payload);

			console.log('CREATE ACTIVITY RESPONSE =>', response);
			
			if (response) {
				resetForm();
				setOpenModal(false);

				setShowSuccess(true);

				setTimeout(() => {
					setShowSuccess(false);
				}, 3000);
				window.location.reload();
			}
		} catch (error: any) {
			setSubmitError(
				error?.response?.data?.message ||
					error?.message ||
					'An activity already exists for the selected schedule.'
			);
		}
		

	};
	const handleCreateActivity = async () => {
		if (isSubmitting) return; // prevent double click
		const isValid = validateForm();

		if (!isValid) return;

		setIsSubmitting(true);

		
		try {
		if (activityType === 'One to One Mentoring') {
			await handleMentoringActivity();
		} else {
			await handleNormalActivity();
		}
	} finally {
		setIsSubmitting(false);
	}
	};

	const showSuccessPopup = (title: string, subtitle: string) => {
		setSuccessTitle(title);
		setSuccessSubtitle(subtitle);
		setShowSuccess(true);

		setTimeout(() => {
			setShowSuccess(false);
		}, 3000);
	};

	return (
		<div className={styles.dashboardContainer}>
			<div>
				{/* TOP STATS */}
				<DashboardStatsContainer />
			</div>
			{/* Calendar Section */}
			<div className={styles.dropdownWrapper}>
				<div className={styles.innerbox}>
					<div className={styles.iconWrapper}>
						<img src={icon} alt={icon} className={styles.icon} />
					</div>
					<div className={styles.programLable}>
						Activities this week
					</div>
				</div>

				<div className={styles.dropdownWrapper2}>
					<button
						className={styles.createActivityBtn}
						onClick={() => setOpenModal(true)}
					>
						Create Activity
						<span className={styles.plusIcon}>＋</span>
					</button>
				</div>
			</div>

			<div className={styles.calendarWrapper}>
				<WeeklySchedule />
			</div>

			{/* MODAL */}
			<CreateActivityModal
				isOpen={openModal}
				disabled={isSubmitting}
				// onClose={() => setOpenModal(false)}
				onClose={() => {
					resetForm();
					setOpenModal(false);
				}} 
				/* PROGRAM */
				programOptions={programOptions}
				selectedProgram={program}
				onProgramSelect={async value => {
					const selectedOption = programOptions.find(
						item => item.value === value
					);

					setProgram(selectedOption?.label || '');
					setProgramId(value);

					setErrors((prev: any) => ({
						...prev,
						program: '',
					}));

					/* RESET MODULE */
					setModule('');
					setModuleId('');

					/* FETCH MODULES */
					const fetchedModules = await fetchProgramModules(value);

					const mappedModules =
						fetchedModules?.map((module: any) => ({
							label: module.title,
							value: module.id,
						})) || [];

					setModuleOptions(mappedModules);

					/* FETCH ENROLLED USERS */
					try {
						const enrollmentResponse =
							await getCourseEnrollments(value);

						if (
							enrollmentResponse?.success &&
							enrollmentResponse.data
						) {
							const mappedParticipants =
								enrollmentResponse.data.map((user: any) => ({
									label: user.username,
									value: user.userid,
								}));

							setParticipantOptions(mappedParticipants);
						}
					} catch (error) {
						console.log('Error fetching participants', error);
					}
				}}
				/* MODULE */
				/* MODULE */
				moduleOptions={moduleOptions}
				selectedModule={module}
				onModuleSelect={value => {
					const selectedModuleOption = moduleOptions.find(
						item => item.value === value
					);

					setModule(selectedModuleOption?.label || '');
					setModuleId(value);
					setErrors((prev: any) => ({
						...prev,
						module: '',
					}));
				}}
				/* ACTIVITY TYPE */
				activityTypeOptions={activityTypeOptions}
				selectedActivityType={activityType}
				// onActivityTypeSelect={setActivityType}
				onActivityTypeSelect={value => {
					const selectedActivityOption = activityTypeOptions.find(
						item => item.value === value
					);

					setActivityType(selectedActivityOption?.label || '');
					setActivityTypeId(value);
					setErrors((prev: any) => ({
						...prev,
						activityType: '',
					}));
				}}
				onNewActivityType={() => setOpenActivityTypeModal(true)}
				/* TITLE */
				activityTitle={title}
				// onActivityTitleChange={setTitle}
				onActivityTitleChange={value => {
					setTitle(value);

					setErrors((prev: any) => ({
						...prev,
						title: '',
					}));
				}}
				/* DESCRIPTION */
				activityDescription={description}
				onActivityDescriptionChange={setDescription}
				/* DATE */
				date={date}
				// onDateChange={setDate}
				onDateChange={value => {
					setDate(value);

					setErrors((prev: any) => ({
						...prev,
						date: '',
					}));
				}}
				/* TIME */
				startTime={startTime}
				endTime={endTime}
				onStartTimeChange={value => {
					setStartTime(value);

					setErrors((prev: any) => ({
						...prev,
						startTime: '',
					}));
				}}
				onEndTimeChange={value => {
					setEndTime(value);

					setErrors((prev: any) => ({
						...prev,
						endTime: '',
					}));
				}}
				
				showParticipants={activityType === 'One to One Mentoring'}
				selectedParticipant={participant}
				participantOptions={participantOptions}
				onParticipantSelect={value => {
					const selectedParticipantOption = participantOptions.find(
						item => item.value === value
					);

					setParticipant(selectedParticipantOption?.label || '');

					setErrors((prev: any) => ({
						...prev,
						participant: '',
					}));
				}}
				/* SUBMIT */
				onSubmit={handleCreateActivity}
				errors={errors}
				submitError={submitError}
			/>

			<NewActivityTypeModal
				isOpen={openActivityTypeModal}
				onClose={() => setOpenActivityTypeModal(false)}
				onSuccess={() => {
					console.log('Activity Type Created');
					setOpenActivityTypeModal(false);

					fetchActivityTypes(); // refresh dropdown

					showSuccessPopup(
						'Activity Type Created Successfully',
						'The new activity type is now available.'
					);
				}}
			/>

			<CoachDashboardActivity />

			{showSuccess && (
				<SuccessContent
					title="Activity Created Successfully"
					subtitle="Your activity has been created."
				/>
			)}
		</div>
	);
};

export default NavCoachMentorDashboard;
